/* eslint-disable @typescript-eslint/no-explicit-any, prefer-rest-params */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID as
  | string
  | undefined;

type AnalyticsValue = string | number | boolean | Array<Record<string, string | number>>;

let initialized = false;

const getGtag = () => {
  if (!MEASUREMENT_ID || typeof window === "undefined") return null;
  const global = window as any;
  if (typeof global.gtag !== "function") {
    global.dataLayer = global.dataLayer || [];
    global.gtag = function () {
      global.dataLayer.push(arguments);
    };
  }
  if (!initialized) {
    global.gtag("js", new Date());
    global.gtag("config", MEASUREMENT_ID, {
      anonymize_ip: true,
      send_page_view: false,
    });
    initialized = true;
  }
  return global.gtag as (...args: any[]) => void;
};

export const trackEvent = (eventName: string, parameters: Record<string, AnalyticsValue> = {}) => {
  const gtag = getGtag();
  if (!gtag) return;
  gtag("event", eventName, { ...parameters, send_to: MEASUREMENT_ID });
};

export const trackCheckoutStart = ({
  itemId,
  itemName,
  value,
  currency,
}: {
  itemId: string;
  itemName: string;
  value: number;
  currency: string;
}) =>
  trackEvent("begin_checkout", {
    currency,
    value,
    items: [{ item_id: itemId, item_name: itemName, price: value, quantity: 1 }],
  });

export const trackConfirmedPurchase = ({
  transactionId,
  itemId,
  itemName,
  value,
  currency,
}: {
  transactionId: string;
  itemId: string;
  itemName: string;
  value: number;
  currency: string;
}) =>
  trackEvent("purchase", {
    transaction_id: transactionId,
    currency,
    value,
    items: [{ item_id: itemId, item_name: itemName, price: value, quantity: 1 }],
  });

const safePageUrl = () => {
  const url = new URL(window.location.href);
  const allowedParams = new URLSearchParams();
  const allowed = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"];
  for (const key of allowed) {
    const value = url.searchParams.get(key);
    if (value) allowedParams.set(key, value);
  }
  return `${url.origin}${url.pathname}${allowedParams.size ? `?${allowedParams.toString()}` : ""}`;
};

export const Analytics = () => {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (!MEASUREMENT_ID) return;
    const page = safePageUrl();
    if (lastPath.current === page) return;
    lastPath.current = page;
    trackEvent("page_view", {
      page_location: page,
      page_path: location.pathname,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!MEASUREMENT_ID) return;
    getGtag();
    const selector = `script[data-ga4-id="${MEASUREMENT_ID}"]`;
    if (document.querySelector(selector)) return;
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    script.dataset.ga4Id = MEASUREMENT_ID;
    const appendScript = () => document.head.appendChild(script);
    if (document.readyState === "complete") {
      const timeout = window.setTimeout(appendScript, 1500);
      return () => window.clearTimeout(timeout);
    }
    let timeout: number | undefined;
    const onLoad = () => {
      timeout = window.setTimeout(appendScript, 1500);
    };
    window.addEventListener("load", onLoad, { once: true });
    return () => {
      window.removeEventListener("load", onLoad);
      if (timeout !== undefined) window.clearTimeout(timeout);
    };
  }, []);

  return null;
};
